import { Button, ButtonProps, Link, Text, useToast } from "@chakra-ui/react";
import React, { Fragment, useState } from "react";
import ShiButton from "src/atoms/ShiButton";
import useMigration from "src/hooks/useMigration";
import MigrationModal from "./MigrationModal";
import { getMigrationProductName } from "src/lib/getMigrationProductName";
import { useLocation } from "@reach/router";

type MigrationButtonProps = ButtonProps & {
  apiEndpoint: string;
};

const getMigrationErrorToast = (productName?: string, status?: number) => {
  const baseToastOptions = {
    status: "error" as const,
    duration: 12000,
    isClosable: true,
  };

  switch (status) {
    case 400:
      return {
        ...baseToastOptions,
        // title: "Erreur 400",
        description: `La migration n'est pas encore disponible pour votre produit. Veuillez patienter et réessayer lorsque vous aurez reçu une communication de Cegid vous invitant à évoluer.`,
      };
    case 401:
      return {
        ...baseToastOptions,
        // title: "Erreur 401",
        description: (
          <Fragment>
            <Text>
              Votre session a expiré. Veuillez retourner sur votre produit{" "}
              {productName} et cliquer à nouveau sur le bouton pour relancer le
              processus de migration.
            </Text>
            <Text mt="space-8">
              Si le problème persiste, contactez notre support:{" "}
              <Link href="mailto:support@shine.co">support@shine.co</Link>
            </Text>
          </Fragment>
        ),
      };
    case 409:
      return {
        ...baseToastOptions,
        description: (
          <Fragment>
            <Text>
              Votre entreprise a déjà été migrée: rendez-vous sur votre compte
              Shine Facture pour la retrouver.
            </Text>
            <Button
              as={Link}
              href="https://app.shine.fr/login"
              variant="primary"
              mt="space-8"
            >
              Accéder à Shine
            </Button>
            <Text mt="space-8">
              Si cela n&apos;est pas le cas et que vous rencontrez un problème,
              contactez notre support:{" "}
              <Link href="mailto:support@shine.co">support@shine.co</Link>
            </Text>
          </Fragment>
        ),
      };
    case 500:
      return {
        ...baseToastOptions,
        // title: "Erreur 500",
        description: (
          <>
            <Text>
              Une erreur inattendue s&apos;est produite. Veuillez réessayer en
              quittant cette page et en cliquant à nouveau sur le bouton de
              migration depuis votre produit {productName}.
            </Text>
            <Text mt="space-8">
              Si le problème persiste, contactez notre support:{" "}
              <Link href="mailto:support@shine.co">support@shine.co</Link>
            </Text>
          </>
        ),
      };
    default:
      return {
        ...baseToastOptions,
        title: "Un problème est survenu",
        description:
          "La migration n'a pas pu démarrer. Veuillez contacter le support client.",
      };
  }
};

const MigrationButton = ({
  apiEndpoint,
  children,
  ...props
}: MigrationButtonProps) => {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { triggerMigration, hasMigrationToken, isLoading } = useMigration();
  const productName = getMigrationProductName(pathname) ?? "";
  const toast = useToast();

  const handleConfirm = async () => {
    const res = await triggerMigration(apiEndpoint);

    if (res?.ok) {
      setIsSuccess(true);
    } else {
      toast(getMigrationErrorToast(productName, res?.status));
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsSuccess(false);
  };

  return (
    <Fragment>
      <MigrationModal
        isOpen={isOpen}
        isSuccess={isSuccess}
        isLoading={isLoading}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      <ShiButton
        hasArrow={false}
        onClick={() => setIsOpen(true)}
        isDisabled={!hasMigrationToken}
        sx={{
          "&[disabled], &[aria-disabled=true]": {
            "&:hover": { background: "btn-green-bg" },
          },
        }}
        {...(props as ButtonProps)}
      >
        {children}
      </ShiButton>
    </Fragment>
  );
};

export default MigrationButton;
